'use client'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'

/**
 * Shared "hotel is gone" failure surface for the checkout flow.
 *
 * Any step can fail because the rate was taken or repriced between screens, and none of
 * them can be retried in place — the itinerary is tied to a rate that no longer exists. So
 * the only useful action is to search again, and the dialog offers just that rather than a
 * back-navigation that would land on a stale page.
 */
const useHotelFailureDialog = (locale: string, isOpen: boolean) => {
    const t = useTranslations('hotels')
    const router = useRouter()

    const searchAgain = useCallback(() => {
        // The search form, not the results page: the SRP needs criteria we cannot rebuild
        // from every failure point, and a prefilled search that fails again is worse than
        // a fresh one.
        router.push(`/${locale}/hotels`)
    }, [router, locale])

    // Shaped for GenericModalWrapper. Not dismissible: closing it would leave the user on a
    // page whose itinerary is already invalid.
    const dialogProps = useMemo(
        () => ({
            open: isOpen,
            primaryText: t('hotelUnavailableTitle'),
            secondaryText: t('hotelUnavailableSubtitle'),
            primaryCTA: t('searchAgain'),
            onPrimaryClick: searchAgain,
            variant: 'error' as const,
            disableBackdropClick: true
        }),
        [isOpen, t, searchAgain]
    )

    return { searchAgain, dialogProps }
}

export default useHotelFailureDialog
